import React from "react";

import { cn } from "../../../common/u1/cn";
import { Input } from "../../../common/u1/Input";
import { SelectField } from "../../../common/u1/SelectField";
import { CountrySelect } from "../../../common/u1/CountrySelect";
import { SearchIcon } from "../../../../assets/ui/icons";
import { EditDialog } from "./EditDialog";
import { OwnershipMeter } from "./OwnershipMeter";
import { useOwnershipForm } from "./useOwnershipForm";

/* Add / edit one owner. Type decides the second field: an individual types a name, a company is PICKED from the business search — which is why that field is... */

/* The two option VALUES are "individual" and "company" and must stay so — the API branches on them, and so does the save path. */
export const OWNER_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "company", label: "Business" },
];

export const OwnershipFormModal = ({
  editing,
  total,
  selectedCompany,
  onClose,
  onSaved,
  onSearchCompany,
}) => {
  const {
    type,
    onTypeChange,
    typeLocked,
    name,
    setName,
    country,
    setCountry,
    countryLocked,
    share,
    setShare,
    errors,
    busy,
    submit,
  } = useOwnershipForm({ editing, total, selectedCompany, onSaved });

  const isCompany = type === "company";
  /* An edit reclaims the share it already holds, so the meter's committed
     figure has that share taken back out of it. */
  const committed = editing
    ? parseFloat((total - parseFloat(editing.percentage_of_shares_held || 0)).toFixed(2))
    : total;

  return (
    <EditDialog
      title={editing ? "Edit Ownership" : "Add New Ownership"}
      onClose={onClose}
      ctaLabel="Save"
      onSubmit={submit}
      submitting={busy}
    >
      <SelectField
        label="Type"
        labelVariant="field"
        id="u1-owner-type"
        value={type}
        onChange={(e) => onTypeChange(e.target.value)}
        options={OWNER_TYPES}
        /* Transcribed: an existing owner's type is fixed. */
        disabled={typeLocked}
      />

      {isCompany ? (
        <div className="mt-3.5">
          {/* No label above it in the design — the field itself says what it
              is until a business is chosen. */}
          <button
            type="button"
            onClick={onSearchCompany}
            disabled={typeLocked}
            /* No aria-label: the visible text is the accessible name in both states — "Search Business" while empty and the business's name once one is chosen. */
            className={cn(
              "flex h-11 w-full items-center gap-2.5 rounded-lg border px-3.5 text-left",
              "text-[14px] font-sans transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
              "disabled:cursor-not-allowed disabled:opacity-60",
              errors.companyName
                ? "border-[var(--coral)] bg-white"
                : "border-[var(--field-border)] bg-[var(--field-inset)] hover:border-[var(--field-border-hover)]",
              name ? "text-[var(--ink)]" : "text-[var(--ink-2)]"
            )}
          >
            <span aria-hidden="true" className="shrink-0 text-[var(--muted-2)]">
              <SearchIcon size={14} />
            </span>
            {/* Transcribed: the name cannot be typed. The live input is
                readOnly and the search step is the only way to set it. */}
            <span className="min-w-0 flex-1 truncate font-semibold">
              {name || "Search Business"}
            </span>
          </button>
          {errors.companyName && (
            <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
              {errors.companyName}
            </p>
          )}
        </div>
      ) : (
        <Input
          wrapperClassName="mt-3.5"
          label="Name"
          id="u1-owner-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          placeholder="Enter full name"
        />
      )}

      {/* The shared country picker, not a native select: 242 countries is far too many to scroll, so it carries its own search, the GCC markets first and a GPS... */}
      <CountrySelect
        className="mt-3.5"
        label="Country"
        labelVariant="field"
        id="u1-owner-country"
        value={country}
        onChange={(c) => setCountry(c?.code)}
        error={errors.country}
        placeholder="Select a country"
        /* Transcribed: fixed once a searched company or an existing owner has
           decided it. */
        disabled={countryLocked}
      />

      <Input
        wrapperClassName="mt-3.5"
        label="Ownership Percentage"
        id="u1-owner-share"
        value={share}
        onChange={(e) => setShare(e.target.value)}
        error={errors.share}
        placeholder="Enter %"
        inputMode="decimal"
      />

      <OwnershipMeter
        className="mt-4"
        total={committed}
        pending={parseFloat(share) || 0}
      />

      {errors.form && (
        <p role="alert" className="mt-3 text-[12px] text-[var(--coral-deep)] font-sans">
          {errors.form}
        </p>
      )}
    </EditDialog>
  );
};

export default OwnershipFormModal;
